import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PullToRefresh } from '@/components/PullToRefresh';
import { MemberAvatar } from '@/components/MemberAvatar';
import { FileText, Users, Share2, TrendingUp, ArrowRight, Clock, Gift } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TeamActivityFeed } from '@/components/TeamActivityFeed';
import Scratchpad from '@/components/Scratchpad';
import { useTour } from '@/contexts/TourContext';
import { DASHBOARD_TOUR } from '@/lib/tourDefinitions';
import logger from '@/lib/logger';

import { StatsCards } from '@/components/dashboard/StatsCards';
import { HottestLeads } from '@/components/dashboard/HottestLeads';
import { FreeAccountsPanel } from '@/components/dashboard/FreeAccountsPanel';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { BuildHealthBadge } from '@/components/dashboard/BuildHealthBadge';
import { AnnualPromoBanner } from '@/components/dashboard/AnnualPromoBanner';
import { OnlineMembersPanel } from '@/components/dashboard/OnlineMembersPanel';
import { RecentCallsPanel } from '@/components/dashboard/RecentCallsPanel';
import { ComplianceHealthWidget } from '@/components/dashboard/ComplianceHealthWidget';
import { WhatsNewModal } from '@/components/WhatsNewModal';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';

const PIE_COLORS = ['#0047AB', '#059669', '#D97706', '#7C3AED', '#DC2626', '#0891B2', '#4F46E5', '#CA8A04'];
const CHART_TICK = { fontSize: 10, fill: '#64748B' };
const CHART_TOOLTIP = { fontSize: 12, borderRadius: 2, border: '1px solid #E2E8F0' };
const CHART_CONTAINER_220 = { minHeight: 220, minWidth: 100 };
const CHART_CONTAINER_200 = { minHeight: 200, minWidth: 100 };

function TrialBanner({ trialDaysLeft }) {
  return (
    <div className="mt-4 p-3 sm:p-4 bg-[#EFF6FF] border border-[#0047AB]/20 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="trial-banner">
      <div>
        <p className="text-sm font-medium text-[#0047AB]">Free Trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining</p>
        <p className="text-xs text-slate-500 mt-0.5">You have full access to all features during your 7-day trial.</p>
      </div>
      <Link to="/billing">
        <span className="bg-[#0047AB] text-white text-xs font-medium px-3 py-1.5 rounded-sm hover:bg-[#003580] transition-colors inline-flex items-center gap-1">
          Subscribe Now <ArrowRight className="w-3 h-3" />
        </span>
      </Link>
    </div>
  );
}

function ExpiredBanner() {
  return (
    <div className="mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="expired-banner">
      <div>
        <p className="text-sm font-medium text-red-700">Your trial has expired</p>
        <p className="text-xs text-red-500 mt-0.5">Subscribe to continue accessing all features.</p>
      </div>
      <Link to="/billing">
        <span className="bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-sm hover:bg-red-700 transition-colors inline-flex items-center gap-1">
          Subscribe <ArrowRight className="w-3 h-3" />
        </span>
      </Link>
    </div>
  );
}

function InactiveBanner() {
  return (
    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between" data-testid="membership-alert">
      <p className="text-sm text-amber-800">Your membership is inactive. Subscribe to access all features.</p>
      <Link to="/billing" className="text-sm font-medium text-[#0047AB] hover:underline flex items-center gap-1">
        Subscribe <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function ChartEmpty({ text }) {
  return <div className="flex items-center justify-center py-16 text-sm text-slate-400">{text}</div>;
}

function LeadsOverTimeChart({ data, margin }) {
  return (
    <div className="lg:col-span-2 border border-slate-200 rounded-sm bg-white">
      <div className="p-4 border-b border-slate-200">
        <h2 className="font-heading font-semibold text-slate-900 text-sm">Leads Created (Last 7 Days)</h2>
      </div>
      <div className="p-4" style={CHART_CONTAINER_220}>
        {data?.some(d => d.count > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={margin}>
              <XAxis dataKey="date" tick={CHART_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Bar dataKey="count" fill="#0047AB" radius={[2, 2, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmpty text="No data for this period" />
        )}
      </div>
    </div>
  );
}

function PieBreakdownChart({ title, data, nameKey, margin }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="p-4 border-b border-slate-200">
        <h2 className="font-heading font-semibold text-slate-900 text-sm">{title}</h2>
      </div>
      <div className="p-4" style={margin ? CHART_CONTAINER_200 : CHART_CONTAINER_220}>
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data} cx="50%" cy="45%" outerRadius={margin ? 60 : 65} innerRadius={margin ? 30 : 35} dataKey="count" nameKey={nameKey} paddingAngle={2}>
                {data.map((entry, i) => <Cell key={`${nameKey}-${entry[nameKey]}-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmpty text={`No ${title.toLowerCase()} data`} />
        )}
        {data?.length > 0 && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 px-4 pb-3 -mt-2">
            {data.map((s, i) => (
              <span key={s[nameKey]} className="flex items-center gap-1 text-[10px] text-slate-500">
                <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                {s[nameKey]} ({s.count})
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChart({ data, margin }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="p-4 border-b border-slate-200">
        <h2 className="font-heading font-semibold text-slate-900 text-sm">Leads by Category</h2>
      </div>
      <div className="p-4" style={CHART_CONTAINER_200}>
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data} layout="vertical" margin={margin}>
              <XAxis type="number" tick={CHART_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="category" tick={CHART_TICK} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Bar dataKey="count" fill="#059669" radius={[0, 2, 2, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmpty text="No categorized leads" />
        )}
      </div>
    </div>
  );
}

function RecentActivity({ activities }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-heading font-semibold text-slate-900 text-sm">Recent Activity</h2>
        <Clock className="w-4 h-4 text-slate-400" />
      </div>
      <div className="p-4">
        {activities?.length > 0 ? (
          <div className="space-y-3">
            {activities.slice(0, 6).map((act, i) => (
              <div key={act.timestamp + act.action} className="flex items-start gap-3 text-sm" data-testid={`activity-${i}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-[#0047AB] mt-1.5 shrink-0" />
                <div>
                  <p className="text-slate-700">{act.details}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{act.user_name} &middot; {new Date(act.timestamp).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">No recent activity</p>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const { startTour, loaded: tourLoaded, isTourCompleted } = useTour();
  const [loading, setLoading] = useState(true);
  const leadsChartMargin = useMemo(() => ({ top: 5, right: 5, left: -20, bottom: 0 }), []);
  const categoryChartMargin = useMemo(() => ({ top: 0, right: 10, left: 0, bottom: 0 }), []);

  const fetchStats = useCallback(() => {
    setLoading(true);
    return api.getStats().then(r => setStats(r.data)).catch(err => logger.error(err)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (tourLoaded && stats && !isTourCompleted('dashboard')) {
      const timer = setTimeout(() => startTour('dashboard', DASHBOARD_TOUR.steps), 1500);
      return () => clearTimeout(timer);
    }
  }, [tourLoaded, stats, isTourCompleted, startTour]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" />
    </div>
  );

  const isTrial = user?.membership_status === 'trial';
  const isExpired = user?.membership_status === 'expired';
  const isFree = user?.membership_status === 'free';
  const isActive = user?.membership_status === 'active' || isFree || ['admin','super_admin'].includes(user?.role);
  const showInactiveBanner = !isTrial && !isExpired && !isActive;

  let trialDaysLeft = 0;
  if (isTrial && user?.membership_expires) {
    trialDaysLeft = Math.max(0, Math.ceil((new Date(user.membership_expires) - new Date()) / (1000 * 60 * 60 * 24)));
  }

  const cards = [
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: FileText, color: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300', cardBg: 'bg-fuchsia-50 border-fuchsia-200 dark:bg-fuchsia-950/30 dark:border-fuchsia-900/50', href: '/leads' },
    { label: 'New Leads', value: stats?.new_leads || 0, icon: TrendingUp, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300', cardBg: 'bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-900/50', href: '/leads?status=New' },
    { label: 'Converted', value: stats?.converted || 0, icon: TrendingUp, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', cardBg: 'bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-900/50', href: '/leads?status=Converted' },
    { label: 'Shared Leads', value: stats?.shared_count || 0, icon: Share2, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', cardBg: 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-900/50', href: '/leads?shared=true' },
  ];
  if (user?.role === 'super_admin') {
    cards.push({ label: 'Total Members', value: stats?.total_members || 0, icon: Users, color: 'bg-[#F5EBE0] text-[#7C5E48] dark:bg-amber-900/40 dark:text-amber-200', cardBg: 'bg-[#F5EBE0] border-[#D9C2A8] dark:bg-amber-950/30 dark:border-amber-900/50', href: '/members' });
    cards.push({ label: 'Active Members', value: stats?.active_members || 0, icon: Users, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', cardBg: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900/50', href: '/members?tab=active' });
    cards.push({ label: 'Free Accounts', value: stats?.free_members || 0, icon: Gift, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', cardBg: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900/50', href: '/members?tab=free' });
  }

  return (
    <PullToRefresh onRefresh={fetchStats}>
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8" data-testid="dashboard-page">
      <WhatsNewModal />
      {/* Welcome Header */}
      <div className="mb-5 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4 mb-4">
          <div className="hidden sm:block"><MemberAvatar userId={user?.id || user?._id} name={user?.name} size="lg" /></div>
          <div className="sm:hidden"><MemberAvatar userId={user?.id || user?._id} name={user?.name} size="md" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium mb-0.5 hidden sm:block">Dashboard</p>
            <h1 className="font-heading text-lg sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight truncate">
              Welcome back, {user?.name}
            </h1>
            <p className="text-[11px] sm:text-sm text-slate-500 mt-0.5 truncate">{user?.email} &middot; <span className="capitalize">{user?.role}</span></p>
          </div>
        </div>
        {isTrial && <TrialBanner trialDaysLeft={trialDaysLeft} />}
        {isExpired && <ExpiredBanner />}
        {showInactiveBanner && <InactiveBanner />}
        {isActive && user?.role !== 'super_admin' && user?.role !== 'admin' && <AnnualPromoBanner />}
      </div>

      <InstallAppPrompt />

      <StatsCards cards={cards} />

      {user?.role === 'super_admin' && <BuildHealthBadge />}

      {user?.role === 'super_admin' && (
        <FreeAccountsPanel freeMembers={stats?.free_members || 0} freeAccounts={stats?.free_accounts || []} />
      )}

      {user?.role === 'super_admin' && (
        <div className="mt-6">
          <ComplianceHealthWidget />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <LeadsOverTimeChart data={stats?.leads_over_time} margin={leadsChartMargin} />
        <PieBreakdownChart title="Lead Status" data={stats?.status_breakdown} nameKey="status" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <CategoryChart data={stats?.category_breakdown} margin={categoryChartMargin} />
        <PieBreakdownChart title="Leads by Source" data={stats?.source_breakdown} nameKey="source" margin />
        <RecentActivity activities={stats?.recent_activities} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2"><TeamActivityFeed /></div>
        <div className="space-y-6">
          <OnlineMembersPanel />
          <RecentCallsPanel />
        </div>
      </div>
      <div className="mb-8"><Scratchpad /></div>
      <HottestLeads leads={stats?.top_leads} />
      <QuickActions />
    </div>
    </PullToRefresh>
  );
}
